import {PosaRedeemStore, RedeemSectionData, GiftcardConfig} from '../../utils/types';
import React, {useCallback, useState, useEffect} from 'react';
import {localizedMessage} from '../message/Message';
import PosaRedeemActions from '../../actions/PosaRedeemActions';
import PosaTemplate from './PosaTemplate';
import PropTypes from 'prop-types';
import RedeemSectionLoggedInStyled from './RedeemSectionLoggedInStyled';
import Tooltip from './Tooltip';
import VerifyingIconStyled from './VerifyingIconStyled';
import cx from 'classnames';
import epicTracking from 'epic-tracking';
import root from 'window-or-global';
import GiftcardBox from './GiftcardBox';
import RedirectWalletOverlay from '../overlay/RedirectWalletOverlay';

interface Props {
  data: RedeemSectionData;
  posaRedeemStore: PosaRedeemStore;
  accountName: string;
  uppercaseCode: boolean;
  prefilledCode: string;
  giftcardConfig: GiftcardConfig;
}

const looseMatch = /^[A-Z|a-z|0-9|-]{1,20}$/;
const strictMatch = /^[A-Z|a-z|0-9|-]{16,20}$/;
const validateCode = (input) => PosaRedeemActions.validateCode(input);
const onKeyPress = (e) => {
  const input = e.target.value.trim();
  if (input.length && e.key === 'Enter' && strictMatch.test(input)) {
    validateCode(input);
  }
};

const LoggedInSection = ({
  data,
  posaRedeemStore,
  accountName,
  uppercaseCode,
  prefilledCode,
  giftcardConfig,
}: Props) => {
  const {verifying, errorCode} = posaRedeemStore;
  const {loggedInContent, toolTipDescription, toolTipImage} = data;
  const {posaGiftCardRedeemUrl, posaGiftCardCardShow} = giftcardConfig;
  const [value, setValue] = useState((prefilledCode || '').trim());
  const [validity, toggleValidity] = useState(true);
  const [showWalletRedirectModal, setShowWalletRedirectModal] = useState(false);

  const onChange = useCallback(
    (e) => {
      const nextValue = uppercaseCode ? e.target.value.toUpperCase().trim() : e.target.value.trim();
      setValue(nextValue.replace(/[^A-Za-z0-9]/g, ''));
      const isValid = looseMatch.test(nextValue);
      if (isValid && errorCode) PosaRedeemActions.resetErrorCode();
      if (isValid !== validity) {
        if (!isValid && nextValue.length > 15) {
          epicTracking.trackEvent('Interaction', {
            interactionType: 'click',
            eventCategory: 'PosaRedeem',
            eventAction: 'codeInvalid',
            length: nextValue.length,
          });
        }
        toggleValidity(!validity);
      }
    },
    [errorCode, uppercaseCode]
  );

  // Handle state(s) when coming back from bfcache — force modal closed
  useEffect(() => {
    if (typeof root !== 'undefined' && root.addEventListener) {
      const handlePageShow = (event) => {
        if (event.persisted) {
          setShowWalletRedirectModal(false);
        }
      };

      root.addEventListener('pageshow', handlePageShow);
      return () => root.removeEventListener('pageshow', handlePageShow);
    }
  }, []);

  useEffect(() => {
    if (errorCode === 'errors.com.epicgames.posa.isWalletCode') {
      setShowWalletRedirectModal(true);
      // Redirect to POSA gift card site in 3s.
      setTimeout(() => {
        root.location.href = `${posaGiftCardRedeemUrl}/${value}`;
      }, 3000);
    }
  }, [errorCode]);

  const showError = !validity || errorCode;
  const errorMsg =
    errorCode && errorCode === localizedMessage(errorCode)
      ? localizedMessage('errors.com.epicgames.posa.cardNotFound')
      : localizedMessage(errorCode ?? '');
  const disabled = !strictMatch.test(value) || verifying;

  const {bgImage, moneyImage} = data;
  const style = React.useMemo(
    () => ({
      backgroundImage: `url(${bgImage})`,
    }),
    [bgImage]
  );
  return (
    <RedeemSectionLoggedInStyled className={'ecr-redeemSection loggedIn'} style={style}>
      <div className="ecrrs-left">
        <img src={moneyImage} alt="Card" />
      </div>
      <div className="ecrrs-right">
        <div className="ecrrs-loggedIn">
          <PosaTemplate
            posaRedeemStore={posaRedeemStore}
            Tag="div"
            string={loggedInContent}
            isTemplate
            accountName={accountName}
          />
          <div className={cx('input-wrapper', {verifying})}>
            {verifying && <VerifyingIconStyled />}
            <input
              className={cx({error: showError})}
              type="text"
              placeholder={localizedMessage(
                'epic.code.redemption.posa.page.redeem.input.placeholder'
              )}
              value={value}
              onChange={onChange}
              onKeyPress={onKeyPress}
            />
            <Tooltip content={toolTipDescription} image={toolTipImage} />
          </div>
          <button
            className={cx('btn btn-primary', {disabled})}
            onClick={() => validateCode(value)}
            disabled={disabled}>
            {localizedMessage('epic.code.redemption.posa.page.common.next')}
          </button>
          <div className={cx('error-msg', {opaque: showError})}>
            <span className="msg">{errorMsg}</span>
          </div>
        </div>

        {posaGiftCardCardShow && (
          <GiftcardBox showDesc={true} posaGiftCardRedeemUrl={posaGiftCardRedeemUrl} />
        )}
      </div>
      {/* The asset below is not needed for localization and will not be added to the current Fortnite.com CMS provider, as it is being deprecated. For now, it will be hardcoded. */}
      {showWalletRedirectModal ? (
        <RedirectWalletOverlay image="https://media-cdn.epicgames.com/8daf628b709c4528931883970208351b/card-art-trimmed.png" />
      ) : null}
    </RedeemSectionLoggedInStyled>
  );
};

LoggedInSection.propTypes = {
  data: PropTypes.object,
  posaRedeemStore: PropTypes.object,
  accountName: PropTypes.string,
  uppercaseCode: PropTypes.bool,
  prefilledCode: PropTypes.string,
  giftcardConfig: PropTypes.object,
};

export default LoggedInSection;
